import * as THREE from "three";
import { LineBuffer, tint, clamp } from "../shared";

function perms(base) {
  const out = new Set();
  const rec = (arr, rest) => { if (!rest.length) { out.add(arr.join(",")); return; } rest.forEach((v, i) => rec([...arr, v], rest.filter((_, j) => j !== i))); };
  rec([], base); return [...out].map((s) => s.split(",").map(Number));
}
function shapeVerts(shape) {
  if (shape === "tesseract") { const v = []; for (let i = 0; i < 16; i++) v.push([0, 1, 2, 3].map((b) => ((i >> b) & 1 ? 1 : -1))); return v; }
  if (shape === "16cell") { const v = []; for (let a = 0; a < 4; a++) for (const s of [1, -1]) { const p = [0, 0, 0, 0]; p[a] = s; v.push(p); } return v; }
  if (shape === "24cell") { const v = []; for (const p of perms([1, 1, 0, 0])) for (const sa of [1, -1]) for (const sb of [1, -1]) { let k = 0; const q = p.map((x) => (x ? x * (k++ === 0 ? sa : sb) : 0)); if (!v.some((w) => w.join() === q.join())) v.push(q); } return v; }
  const r5 = Math.sqrt(5);
  return [[1, 1, 1, -1 / r5], [1, -1, -1, -1 / r5], [-1, 1, -1, -1 / r5], [-1, -1, 1, -1 / r5], [0, 0, 0, r5 - 1 / r5]];
}
function build(shape) {
  const v = shapeVerts(shape), d = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2], a[3] - b[3]);
  let mn = Infinity; for (let i = 0; i < v.length; i++) for (let j = i + 1; j < v.length; j++) mn = Math.min(mn, d(v[i], v[j]));
  const e = []; for (let i = 0; i < v.length; i++) for (let j = i + 1; j < v.length; j++) if (Math.abs(d(v[i], v[j]) - mn) < 1e-3) e.push([i, j]);
  const R = Math.max(...v.map((p) => Math.hypot(...p)));
  return { v: v.map((p) => p.map((x) => x / R)), e };
}
const rot = (p, a, b, t) => { const c = Math.cos(t), s = Math.sin(t), x = p[a], y = p[b]; p[a] = x * c - y * s; p[b] = x * s + y * c; };

class Dimension {
  constructor(ctx) {
    this.ctx = ctx; this.writesColors = true; this.ang = { xw: 0, yw: 0, zw: 0, xy: 0 };
    this.wire = new LineBuffer(ctx.overlay, 200, 1);
    this.dots = new THREE.InstancedMesh(new THREE.SphereGeometry(0.07, 12, 8), new THREE.MeshBasicMaterial({ color: 0xffffff }), 24);
    ctx.overlay.add(this.dots); this.wv = new Float32Array(ctx.count); this.setShape();
  }
  setShape() { this.geo = build(this.ctx.params.shape); }
  resize() { this.wv = new Float32Array(this.ctx.count); }
  pointer(e) {
    if (e.type === "down") this.last = e;
    else if (e.type === "move" && this.last) { this.ang.xw += (e.x - this.last.x) * 0.5; this.ang.yw += (e.z - this.last.z) * 0.5; this.last = e; }
    else this.last = null;
  }
  step(dt) {
    const P = this.ctx.params, a = this.ang, { N, sp, half, px, target, colors } = this.ctx, wv = this.wv;
    a.xw += P.rxw * dt; a.yw += P.ryw * dt; a.zw += P.rzw * dt; a.xy += P.rxy * dt;
    const sc = P.scale * this.ctx.g.force, base = this.ctx.top + 2.4, m = new THREE.Matrix4();
    const pts = this.geo.v.map((v0) => {
      const p = [...v0]; rot(p, 0, 3, a.xw); rot(p, 1, 3, a.yw); rot(p, 2, 3, a.zw); rot(p, 0, 1, a.xy);
      const f = P.dist / (P.dist - p[3]); return [p[0] * f * sc, p[1] * f * sc, p[2] * f * sc, p[3]];
    });
    this.dots.count = P.wire ? pts.length : 0;
    pts.forEach((p, i) => { m.makeTranslation(p[0], base + p[1], p[2]); this.dots.setMatrixAt(i, m); });
    this.dots.instanceMatrix.needsUpdate = true;
    const W = this.wire; W.begin();
    const col = (w) => (w >= 0 ? [0.1 + w * 0.2, 0.94, 1] : [1, 0.72 + w * 0.2, 0.1]);
    target.fill(-0.85); wv.fill(0);
    const sig = P.width, R = sig * 2.5, inv = 1 / (sig * sig), yMax = sc * 2;
    for (const [i, j] of this.geo.e) {
      const A = pts[i], B = pts[j];
      if (P.wire) { const ca = col(A[3]), cb = col(B[3]); W.seg(A[0], base + A[1], A[2], B[0], base + B[1], B[2], ...ca, ...cb); }
      const dx = B[0] - A[0], dz = B[2] - A[2], l2 = dx * dx + dz * dz || 1e-6;
      const c0 = clamp(Math.floor((Math.min(A[0], B[0]) - R + half) / sp), 0, N - 1), c1 = clamp(Math.ceil((Math.max(A[0], B[0]) + R + half) / sp), 0, N - 1);
      const r0 = clamp(Math.floor((Math.min(A[2], B[2]) - R + half) / sp), 0, N - 1), r1 = clamp(Math.ceil((Math.max(A[2], B[2]) + R + half) / sp), 0, N - 1);
      for (let r = r0; r <= r1; r++) for (let c = c0; c <= c1; c++) {
        const t = clamp(((px[c] - A[0]) * dx + (px[r] - A[2]) * dz) / l2, 0, 1), qx = A[0] + dx * t - px[c], qz = A[2] + dz * t - px[r];
        const g = Math.exp(-(qx * qx + qz * qz) * inv); if (g < 0.02) continue;
        const y = A[1] + (B[1] - A[1]) * t, hgt = -0.85 + 1.85 * g * (0.45 + 0.55 * clamp((y + yMax) / (2 * yMax), 0, 1)), k = r * N + c;
        if (hgt > target[k]) { target[k] = hgt; wv[k] = A[3] + (B[3] - A[3]) * t; }
      }
    }
    W.end();
    for (let k = 0; k < target.length; k++) tint(colors, k, target[k] > -0.8 ? (wv[k] >= 0 ? 0.4 + wv[k] : -0.4 + wv[k]) : 0, 0.9);
  }
  onParams(key) { if (key === "shape") this.setShape(); }
  snapshot() { return { ...this.ang }; }
  restore(s) { this.ang = { ...s }; }
  clear() { this.ang = { xw: 0, yw: 0, zw: 0, xy: 0 }; }
  telemetry() {
    const d = (x) => `${Math.round(((x * 180) / Math.PI) % 360)}°`;
    return [["Polytope", { tesseract: "Tesseract", "16cell": "16-cell", "24cell": "24-cell", "5cell": "5-cell" }[this.ctx.params.shape]], ["V / E", `${this.geo.v.length} / ${this.geo.e.length}`], ["XW", d(this.ang.xw)], ["YW", d(this.ang.yw)]];
  }
  dispose() { this.wire.dispose(); this.ctx.overlay.remove(this.dots); this.dots.geometry.dispose(); this.dots.material.dispose(); }
}

const dimension = {
  id: "dimension-lab", name: "Dimension Lab", icon: "Box", rest: -0.85,
  desc: "Four-dimensional polytopes rotate through the planes XW, YW, ZW and XY, are perspective-projected into 3D (floating wireframe), then cast a glowing shadow below. Drag across the board to rotate through the 4th dimension. Cyan = +W, amber = −W.",
  math: "P₃ = (x, y, z) · d / (d − w)",
  params: [
    { key: "shape", label: "Polytope", type: "select", options: [["tesseract", "Tesseract"], ["16cell", "16-cell"], ["24cell", "24-cell"], ["5cell", "5-cell"]], def: "tesseract" },
    { key: "rxw", label: "XW rotation speed", type: "range", min: -1.5, max: 1.5, step: 0.01, def: 0.45 },
    { key: "ryw", label: "YW rotation speed", type: "range", min: -1.5, max: 1.5, step: 0.01, def: 0.2 },
    { key: "rzw", label: "ZW rotation speed", type: "range", min: -1.5, max: 1.5, step: 0.01, def: 0 },
    { key: "rxy", label: "XY rotation speed", type: "range", min: -1.5, max: 1.5, step: 0.01, def: 0.12 },
    { key: "dist", label: "4D camera distance", type: "range", min: 1.6, max: 6, step: 0.05, def: 2.6 },
    { key: "scale", label: "Projection scale", type: "range", min: 0.8, max: 3.5, step: 0.05, def: 2 },
    { key: "width", label: "Shadow width", type: "range", min: 0.05, max: 0.6, step: 0.01, def: 0.22 },
    { key: "wire", label: "Floating 3D wireframe", type: "toggle", def: true },
  ],
  actions: [],
  create: (ctx) => new Dimension(ctx),
};
export default dimension;
